import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import SectionChip from '../components/SectionChip';
import { DataTable } from '../components/table/DataTable';
import { formatTimestamp } from '../utils/date-utils';
import BoxEmptyDialog from './BoxEmptyDialog';
import { itemName, TOUCH } from './PoolLines';
import { BTN_ROW_TEXT } from './WorkOrderBits';
import { buttonClass } from '../components/ui/Button';
import { ListRow, ListStack } from '../components/ui/ListRow';
import Reveal from '../components/ui/Reveal';
import type { BoxItem, PoolPickedBox } from './workOrdersApi';

export interface PickedMeta {
  expandedHolds: ReadonlySet<string>;
  onToggleHolds: (barcode: string) => void;
  canPick: boolean;
  busy: boolean;
  onEmptied: () => void;
}

const metaOf = (m: unknown) => m as PickedMeta;

/** What the runs need first, then whatever else shares the box. */
const sortedItems = (items: BoxItem[] | null) =>
  (items || []).slice().sort((x, y) => Number(y.on_this_line) - Number(x.on_this_line));

/** Past this many items a box shows the first few and a toggle for the rest. */
const HOLDS_CAP = 4;
const HOLDS_SHOWN = 3;

const Holds: React.FC<{ box: PoolPickedBox; m: PickedMeta; card?: boolean }> = ({ box: b, m, card }) => {
  const items = sortedItems(b.items);
  const open = m.expandedHolds.has(b.barcode);
  const capped = items.length > HOLDS_CAP;
  const full = items.map((it) => `${it.quantity_remaining} ${itemName(it)}`).join('\n');
  const line = (it: BoxItem) => (
    <span key={it.receipt_item_id ?? it.sku} className={`block truncate ${it.on_this_line ? 'text-on-surface' : 'text-on-surface-variant'}`}>
      <span className="tabular-nums">{it.quantity_remaining}</span> {itemName(it)}
    </span>
  );
  return (
    <div className="min-w-0">
      <span title={full} className="block min-w-0">
        {(capped ? items.slice(0, HOLDS_SHOWN) : items).map(line)}
      </span>
      {capped && (
        <Reveal show={open}>
          <span title={full} className="block min-w-0">{items.slice(HOLDS_SHOWN).map(line)}</span>
        </Reveal>
      )}
      {items.length > HOLDS_CAP && (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => m.onToggleHolds(b.barcode)}
          className={`${BTN_ROW_TEXT} -ml-2.5 ${card ? TOUCH : ''}`}
        >
          {open ? 'Show fewer' : `${items.length - HOLDS_SHOWN} more`}
        </button>
      )}
    </div>
  );
};

const pickedWhen = (b: PoolPickedBox) => (b.picked_at ? formatTimestamp(b.picked_at, 'MMM D, h:mm A') : null);

/** Where the box came from, and how it goes back: the desktop never returns a box. */
const BackNote: React.FC<{ box: PoolPickedBox }> = ({ box: b }) => (
  <div className="min-w-0 text-xs text-on-surface-variant">
    {b.came_from && (
      <div className="flex flex-wrap items-center gap-1.5">
        <span>Came from</span>
        <SectionChip code={b.came_from.code} />
      </div>
    )}
    <div>Put away with the handheld scanner.</div>
  </div>
);

const BoxActions: React.FC<{ box: PoolPickedBox; m: PickedMeta; card?: boolean }> = ({ box: b, m, card }) => (
  <div className={`flex items-center gap-1 ${card ? '-ml-2.5' : 'justify-end'}`}>
    <BoxEmptyDialog
      barcode={b.barcode}
      triggerLabel="Mark empty"
      triggerAriaLabel={`Mark ${b.barcode} empty`}
      triggerClassName={`${buttonClass('quiet', 'sm')} ${card ? TOUCH : ''}`}
      disabled={m.busy}
      onDone={m.onEmptied}
    />
  </div>
);

const COLUMNS: ColumnDef<PoolPickedBox, any>[] = [
  {
    id: 'box',
    header: 'Box',
    accessorFn: (b: PoolPickedBox) => b.barcode,
    cell: ({ row }) => <span className="whitespace-nowrap font-mono text-on-surface">{row.original.barcode}</span>,
  },
  {
    id: 'holds',
    header: 'Holds',
    meta: { cellClass: 'min-w-[12rem] max-w-[22rem]' },
    cell: ({ row, table }) => <Holds box={row.original} m={metaOf(table.options.meta)} />,
  },
  {
    id: 'left',
    header: 'Left',
    accessorFn: (b: PoolPickedBox) => b.quantity_remaining,
    meta: { align: 'right', numeric: true },
  },
  {
    id: 'picked',
    header: 'Picked',
    accessorFn: (b: PoolPickedBox) => b.picked_at ?? '',
    cell: ({ row }) => {
      const b = row.original;
      const when = pickedWhen(b);
      if (!when) return <span className="text-on-surface-variant">—</span>;
      return (
        <div className="whitespace-nowrap">
          <div className="tabular-nums text-on-surface">{when}</div>
          {b.picked_by_name && <div className="text-xs text-on-surface-variant">{b.picked_by_name}</div>}
        </div>
      );
    },
  },
  {
    id: 'back',
    header: 'Came from',
    cell: ({ row }) => <BackNote box={row.original} />,
  },
  {
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { align: 'right' },
    cell: ({ row, table }) => {
      const m = metaOf(table.options.meta);
      return m.canPick ? <BoxActions box={row.original} m={m} /> : null;
    },
  },
];

const getBarcode = (b: PoolPickedBox) => b.barcode;

const PickedCard: React.FC<{ box: PoolPickedBox; m: PickedMeta }> = ({ box: b, m }) => {
  const when = pickedWhen(b);
  return (
    <ListRow className="text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-mono text-on-surface">{b.barcode}</span>
        <span className="whitespace-nowrap text-xs tabular-nums text-on-surface-variant">{b.quantity_remaining} left</span>
      </div>
      {when && (
        <div className="mt-0.5 text-xs tabular-nums text-on-surface-variant">
          {[when, b.picked_by_name].filter(Boolean).join(', ')}
        </div>
      )}
      <div className="mt-1.5 text-xs">
        <Holds box={b} m={m} card />
      </div>
      <div className="mt-1.5">
        <BackNote box={b} />
      </div>
      {m.canPick && (
        <div className="mt-1">
          <BoxActions box={b} m={m} card />
        </div>
      )}
    </ListRow>
  );
};

interface Props {
  picked: PoolPickedBox[];
  meta: PickedMeta;
  wide: boolean;
  status: React.ReactNode;
  empty: React.ReactNode;
}

/** Every box in Assembly, with the section it came from and its write-off. */
const PoolPicked: React.FC<Props> = ({ picked, meta, wide, status, empty }) => {
  if (wide) {
    return (
      <DataTable<PoolPickedBox>
        data={picked}
        columns={COLUMNS}
        meta={meta}
        getRowId={getBarcode}
        strip={status}
        empty={empty}
      />
    );
  }
  return (
    <div className="px-3 pb-3">
      <p className="px-1 pb-2">{status}</p>
      {picked.length === 0 ? (
        <div className="px-1">{empty}</div>
      ) : (
        <ListStack
          items={picked}
          getKey={getBarcode}
          on="panel"
          spacing="loose"
          renderRow={(b) => <PickedCard box={b} m={meta} />}
        />
      )}
    </div>
  );
};

export default PoolPicked;
